import { useMutation as usePostClickedNotification } from "@faire/web-api--source/endpoints/www/api/retailer/notifications/click/post-hooks";
import { useMutation as usePostDismissedNudge } from "@faire/web-api--source/endpoints/www/api/retailer/notifications/dismiss-nudge/post-hooks";
import { useMutation as usePostDismissedNotification } from "@faire/web-api--source/endpoints/www/api/retailer/notifications/dismiss/post-hooks";
import { useInvalidateQuery as useInvalidateGetNotificationQuery } from "@faire/web-api--source/endpoints/www/api/retailer/notifications/get-hooks";
import { useMutation as usePostViewedNotification } from "@faire/web-api--source/endpoints/www/api/retailer/notifications/view/post-hooks";
import { IClickNotificationRequest } from "@faire/web-api--source/indigofair/retailer/IClickNotificationRequest";
import { IDismissNotificationNudgeRequest } from "@faire/web-api--source/indigofair/retailer/IDismissNotificationNudgeRequest";
import { IDismissNotificationRequest } from "@faire/web-api--source/indigofair/retailer/IDismissNotificationRequest";
import { IViewNotificationRequest } from "@faire/web-api--source/indigofair/retailer/IViewNotificationRequest";
import { logError } from "@faire/web/common/logging";
import { useCallback } from "react";

export const useSetNotificationState = (token: string) => {
  const { mutateAsync: viewedNotification } = usePostViewedNotification();
  const { mutateAsync: dismissedNotification } = usePostDismissedNotification();
  const { mutateAsync: dismissedNudge } = usePostDismissedNudge();
  const { mutateAsync: clickedNotification } = usePostClickedNotification();
  const invalidateGetNotificationQuery = useInvalidateGetNotificationQuery();

  const setNotificationHasBeenViewed = useCallback(async () => {
    try {
      await viewedNotification([
        IViewNotificationRequest.build({
          notification_token: token,
        }),
      ]);
    } catch (error) {
      logError("Unable to set notification as viewed");
    }
  }, [token, viewedNotification]);

  const setNotificationHasBeenDismissed = useCallback(async () => {
    try {
      await dismissedNotification(
        [
          IDismissNotificationRequest.build({
            notification_token: token,
          }),
        ],
        {
          onSuccess: () => invalidateGetNotificationQuery(),
        }
      );
    } catch (error) {
      logError("Unable to set notification as dismissed");
    }
  }, [dismissedNotification, invalidateGetNotificationQuery, token]);

  const setNudgeHasBeenDismissed = useCallback(async () => {
    try {
      await dismissedNudge(
        [
          IDismissNotificationNudgeRequest.build({
            notification_token: token,
          }),
        ],
        {
          onSuccess: () => invalidateGetNotificationQuery(),
        }
      );
    } catch (error) {
      logError("Unable to set nudge as dismissed");
    }
  }, [dismissedNudge, invalidateGetNotificationQuery, token]);

  const setNotificationHasBeenClicked = useCallback(async () => {
    try {
      await clickedNotification([
        IClickNotificationRequest.build({
          notification_token: token,
        }),
      ]);
    } catch (error) {
      logError("Unable to set notification as clicked");
    }
  }, [clickedNotification, token]);

  return {
    setNotificationHasBeenViewed,
    setNotificationHasBeenDismissed,
    setNotificationHasBeenClicked,
    setNudgeHasBeenDismissed,
  };
};
